import {createRef, ReactElement, RefObject} from 'react'
import {autorun, observable, reaction, toJS} from 'mobx'
import {get, isString, pickBy, reduce, startsWith, uniq} from 'lodash'
import {TOptions} from 'i18next'
import {
  AllEpikQueryParams,
  CustomerInfo,
  Epik,
  EpikEvent,
  EpikEventName,
  EpikQueryParam,
  EpmsPaymentInfo,
  EpmsPaymentSourceInfo,
  EpmsPaymentStatus,
  EpmsSubscriptionInfo,
  isCreditCardPaymentMethod,
  PaymentError,
  PaymentMethod,
  PaymentProvider,
  RouterLocation,
  SubscriptionInfo,
  SubscriptionStatus,
  TransactionInfo,
  TransactionStatus,
} from '@raisenow/epik'
import {Compiler, FunctionMocks} from './Compiler'
import {Flags} from './Flags'
import {PaymentForm} from './forms/PaymentForm'
import {SubscriptionRenewForm} from './forms/SubscriptionRenewForm'
import {WidgetConfig} from './WidgetConfig'
import {WidgetFlag} from './WidgetFlag'
import {Translator} from './Translator'
import {Validator} from './Validator'
import {WidgetUi} from './WidgetUi'
import {WidgetEventName, WidgetEvents} from './WidgetEventer'
import {WidgetProps} from '../components/Widget'
import {formatAmount, prepareWidgetLocation, subunitsToUnits} from './helpers'

///////////////////////////////////////////////////////////////////////////////

export type WidgetApiArgs = {
  config: WidgetConfig
  computedConfig: WidgetConfig
  events: WidgetEvents
  compiler: Compiler
  translator: Translator
  validator: Validator
  epik: Epik
  ui: WidgetUi
  flags: Flags
  paymentForm: PaymentForm
  subscriptionRenewForm: SubscriptionRenewForm
}

export type WidgetRecaptcha = {
  ref: RefObject<any>
  callback: (token) => void
  promise: Promise<string>
  resolve: (token: string) => void
}

export type WidgetLocation = Partial<RouterLocation> & {
  params?: {
    [key: string]: any
  }
}

///////////////////////////////////////////////////////////////////////////////

export class WidgetApi {
  node: ReactElement<WidgetProps>
  widgetContentRef: RefObject<HTMLDivElement>
  @observable transactionInfo: TransactionInfo
  @observable subscriptionInfo: SubscriptionInfo
  @observable customerInfo: CustomerInfo
  @observable epmsPaymentInfo: EpmsPaymentInfo
  @observable epmsSubscriptionInfo: EpmsSubscriptionInfo
  @observable epmsPaymentSourceInfo: EpmsPaymentSourceInfo
  events: WidgetEvents
  config: WidgetConfig
  computedConfig: WidgetConfig
  compiler: Compiler
  translator: Translator
  validator: Validator
  epik: Epik
  ui: WidgetUi
  flags: Flags
  paymentForm: PaymentForm
  subscriptionRenewForm: SubscriptionRenewForm
  @observable recaptcha: WidgetRecaptcha
  @observable epmsPaymentRequestData: any
  @observable epmsPaymentResponseData: any
  @observable epmsPaymentResponseError: any
  @observable currentLocation: WidgetLocation

  constructor(args: WidgetApiArgs) {
    let {
      config,
      computedConfig,
      events,
      compiler,
      translator,
      validator,
      epik,
      ui,
      flags,
      paymentForm,
      subscriptionRenewForm,
    } = args

    this.node = null
    this.widgetContentRef = createRef<HTMLDivElement>()
    this.transactionInfo = null
    this.subscriptionInfo = null
    this.customerInfo = null
    this.epmsPaymentInfo = null
    this.epmsSubscriptionInfo = null
    this.epmsPaymentSourceInfo = null
    this.config = config
    this.computedConfig = computedConfig
    this.events = events
    this.compiler = compiler
    this.translator = translator
    this.validator = validator
    this.epik = epik
    this.ui = ui
    this.flags = flags
    this.paymentForm = paymentForm
    this.subscriptionRenewForm = subscriptionRenewForm
    this.epmsPaymentRequestData = null
    this.epmsPaymentResponseData = null
    this.epmsPaymentResponseError = null
    this.currentLocation = {}
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // INIT
  //
  /////////////////////////////////////////////////////////////////////////////

  async init() {
    this.initRecaptcha()
    this.registerCompilerHelpers()
    this.initDynamicFlags()

    this.paymentForm.initDefaults()
    this.paymentForm.restorePersistedData()

    this.initLocationChangeHandling()
    this.initEventsHandling()
    this.initParamsReactions()
    this.initRefnoHandling()

    this.epik.init()
    this.handleLocationChange()
  }

  initLocationChangeHandling() {
    this.epik.router.listen(this.handleLocationChange.bind(this))
  }

  handleLocationChange() {
    this.currentLocation = prepareWidgetLocation(this.epik.router)
  }

  initEventsHandling() {
    this.epik.events[EpikEventName.FETCH_PAYMENT_DATA_START].subscribe(
      async () => {
        this.flags.remove(WidgetFlag.SHOW_SPINNER_OUTER)
        this.flags.add(WidgetFlag.SHOW_SPINNER_INNER)
        this.paymentForm.data.epms_subscription_uuid = null
        await this.events[WidgetEventName.FETCH_PAYMENT_DATA_START].publish({
          api: this,
        })
      },
    )

    this.epik.events[EpikEventName.FETCH_PAYMENT_DATA_END].subscribe(
      async (event: EpikEvent) => {
        let {
          transactionInfo,
          subscriptionInfo,
          customerInfo,
          epmsPaymentInfo,
          epmsSubscriptionInfo,
          epmsPaymentSourceInfo,
          transactionError,
          subscriptionError,
          customerError,
          epmsPaymentError,
          epmsSubscriptionError,
          epmsPaymentSourceError,
        } = event.data

        if (transactionError) {
          this.flags.add(WidgetFlag.TRANSACTION_NOT_FOUND)
        } else {
          this.flags.remove(WidgetFlag.TRANSACTION_NOT_FOUND)
        }

        if (subscriptionError) {
          this.flags.add(WidgetFlag.SUBSCRIPTION_NOT_FOUND)
        } else {
          this.flags.remove(WidgetFlag.SUBSCRIPTION_NOT_FOUND)
        }

        if (customerError) {
          this.flags.add(WidgetFlag.CUSTOMER_INFO_NOT_FOUND)
        } else {
          this.flags.remove(WidgetFlag.CUSTOMER_INFO_NOT_FOUND)
        }

        if (epmsPaymentError) {
          this.flags.add(WidgetFlag.EPMS_PAYMENT_NOT_FOUND)
        } else {
          this.flags.remove(WidgetFlag.EPMS_PAYMENT_NOT_FOUND)
        }

        if (epmsSubscriptionError) {
          this.flags.add(WidgetFlag.EPMS_SUBSCRIPTION_NOT_FOUND)
        } else {
          this.flags.remove(WidgetFlag.EPMS_SUBSCRIPTION_NOT_FOUND)
        }

        if (epmsPaymentSourceError) {
          this.flags.add(WidgetFlag.EPMS_PAYMENT_SOURCE_NOT_FOUND)
        } else {
          this.flags.remove(WidgetFlag.EPMS_PAYMENT_SOURCE_NOT_FOUND)
        }

        this.setTransactionInfo(transactionInfo)
        this.setSubscriptionInfo(subscriptionInfo)
        this.setCustomerInfo(customerInfo)
        this.setEpmsPaymentInfo(epmsPaymentInfo)
        this.setEpmsSubscriptionInfo(epmsSubscriptionInfo)
        this.setEpmsPaymentSourceInfo(epmsPaymentSourceInfo)

        this.flags.remove(WidgetFlag.SHOW_SPINNER_INNER)

        await this.events[WidgetEventName.FETCH_PAYMENT_DATA_END].publish({
          api: this,
        })
        await this.tryEmitPaymentCompleteEvent()
      },
    )

    this.epik.events[EpikEventName.FETCH_PAYMENT_DATA_END].subscribe(
      async (event: EpikEvent) => {
        this.epmsPaymentRequestData = null
        this.epmsPaymentResponseData = null
        this.epmsPaymentResponseError = null

        this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_REQUEST_DATA)
        this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_DATA)
        this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_ERROR)
      },
    )

    this.epik.events[EpikEventName.TRANSACTION_STATUS_POLL].subscribe(
      async (event: EpikEvent) => {
        let transactionInfo = event.data.transactionInfo

        if (
          transactionInfo &&
          transactionInfo.epayment_status !== TransactionStatus.PENDING
        ) {
          this.setTransactionInfo(transactionInfo)

          await this.tryEmitPaymentCompleteEvent()
        }
      },
    )

    this.epik.events[EpikEventName.SEND_EPMS_PAYMENT_END].subscribe(
      async (event: EpikEvent) => {
        let {
          epmsPaymentRequestData,
          epmsPaymentResponseData,
          epmsPaymentResponseError,
        } = event.data

        this.epmsPaymentRequestData = epmsPaymentRequestData
          ? epmsPaymentRequestData
          : null
        this.epmsPaymentResponseData = epmsPaymentResponseData
          ? epmsPaymentResponseData
          : null
        this.epmsPaymentResponseError = epmsPaymentResponseError
          ? epmsPaymentResponseError
          : null

        if (epmsPaymentResponseData) {
          this.flags.add(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_DATA)
        } else {
          this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_DATA)
        }

        if (epmsPaymentResponseError) {
          this.flags.add(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_ERROR)
        } else {
          this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_ERROR)
        }

        if (epmsPaymentRequestData) {
          this.flags.add(WidgetFlag.HAS_EPMS_PAYMENT_REQUEST_DATA)
        } else {
          this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_REQUEST_DATA)
        }

        this.flags.remove(WidgetFlag.SHOW_SPINNER_OUTER)
        this.flags.remove(WidgetFlag.SHOW_SPINNER_INNER)
      },
    )
  }

  initParamsReactions() {
    reaction(
      () => {
        let params = pickBy(toJS(this.currentLocation.params), (v, k) => {
          return startsWith(k, 'rnw-') && k !== EpikQueryParam.VIEW
        })

        return JSON.stringify(params)
      },
      (paramsJSON) => {
        let params = JSON.parse(paramsJSON)

        this.paymentForm.updateDataFromQueryParams(params)
      },
      {
        fireImmediately: false,
      },
    )
  }

  initRefnoHandling() {
    // Clear refno if paymentMethod/campaignId changes
    reaction(
      () =>
        JSON.stringify({
          paymentMethod: this.paymentForm.getPaymentMethod(),
          campaignId: this.paymentForm.data.stored_campaign_id,
        }),
      () => {
        this.paymentForm.data.refno = null
      },
      {
        fireImmediately: true,
      },
    )

    // Fetch refno before validation if its not set
    this.events[WidgetEventName.BEFORE_PAYMENT_VALIDATE].subscribe(async () => {
      const isEzs =
        this.paymentForm.getPaymentMethod() ===
        PaymentMethod.PAYMENT_SLIP_WITH_REFERENCE_NUMBER
      const hasRefno = !!this.paymentForm.data.refno

      if (isEzs && !hasRefno) {
        this.computedConfig.debug && console.log('[tamaro] fetching refno')

        try {
          this.paymentForm.data.refno = await this.epik.fetchReferenceNumber(
            this.paymentForm.data.stored_campaign_id,
          )
        } catch (error) {}
      }
    })

    // Show error screen if there is only one validation error – refno-related
    this.events[WidgetEventName.PAYMENT_VALIDATE_ERROR].subscribe(async () => {
      const errors = toJS(this.paymentForm.validationErrors || {})

      if (
        Object.keys(errors).length === 1 &&
        errors.refno?.length === 1 &&
        errors.refno[0].code === PaymentError.REFNO_MISSING.code
      ) {
        this.flags.add(WidgetFlag.PAYMENT_ABORTED)
        await this.ui.scrollToTop()
      }
    })
  }

  /////////////////////////////////////////////////////////////////////////////

  async retryPayment() {
    this.epik.transactionStatusPoll.stop()
    this.epmsPaymentRequestData = null
    this.epmsPaymentResponseData = null
    this.epmsPaymentResponseError = null
    this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_REQUEST_DATA)
    this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_DATA)
    this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_ERROR)

    this.flags.remove(WidgetFlag.PAYMENT_ABORTED)
    this.paymentForm.validationErrors = null

    let params = reduce(
      AllEpikQueryParams,
      (result, v) => {
        result[v] = undefined

        return result
      },
      {},
    )

    this.epik.updateCurrentUrl({params})
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // SETTERS (transaction, subscription, customer, epmsPayment info)
  //
  /////////////////////////////////////////////////////////////////////////////

  setTransactionInfo(transactionInfo: TransactionInfo) {
    if (transactionInfo) {
      transactionInfo.amount = subunitsToUnits(
        transactionInfo.amount,
        transactionInfo.currency,
        this.computedConfig,
      )

      this.paymentForm.updateData(transactionInfo)
    }

    this.transactionInfo = transactionInfo
  }

  setSubscriptionInfo(subscriptionInfo: SubscriptionInfo) {
    if (subscriptionInfo) {
      subscriptionInfo.amount = subunitsToUnits(
        subscriptionInfo.amount,
        subscriptionInfo.currency,
        this.computedConfig,
      )

      this.paymentForm.updateData(subscriptionInfo)
      this.subscriptionRenewForm.updateData(subscriptionInfo)

      if (subscriptionInfo.customer) {
        this.paymentForm.updateData(subscriptionInfo.customer)
        this.subscriptionRenewForm.updateData(subscriptionInfo.customer)
      }

      if (subscriptionInfo.status === SubscriptionStatus.INACTIVE) {
        // skip in case of mpos payment method
        if (subscriptionInfo.payment_method !== PaymentMethod.MPOS) {
          this.flags.add(WidgetFlag.SUBSCRIPTION_NOT_FOUND)
        }
      }
    }

    this.subscriptionInfo = subscriptionInfo
  }

  setCustomerInfo(customerInfo: CustomerInfo) {
    if (customerInfo) {
      this.paymentForm.updateData(customerInfo)
      this.subscriptionRenewForm.updateData(customerInfo)
    }

    this.customerInfo = customerInfo
  }

  setEpmsPaymentInfo(epmsPaymentInfo: EpmsPaymentInfo) {
    if (epmsPaymentInfo) {
      this.paymentForm.updateDataFromEpmsPayment(epmsPaymentInfo)
    }

    this.epmsPaymentInfo = epmsPaymentInfo
  }

  setEpmsSubscriptionInfo(epmsSubscriptionInfo: EpmsSubscriptionInfo) {
    if (epmsSubscriptionInfo) {
      this.paymentForm.data.epms_subscription_uuid = epmsSubscriptionInfo.uuid
    }

    this.epmsSubscriptionInfo = epmsSubscriptionInfo
  }

  setEpmsPaymentSourceInfo(epmsPaymentSourceInfo: EpmsPaymentSourceInfo) {
    this.epmsPaymentSourceInfo = epmsPaymentSourceInfo
  }

  /////////////////////////////////////////////////////////////////////////////

  private initDynamicFlags() {
    autorun(() => {
      if (this.transactionInfo) {
        this.flags.add(WidgetFlag.HAS_TRANSACTION_INFO)
      } else {
        this.flags.remove(WidgetFlag.HAS_TRANSACTION_INFO)
      }

      if (this.subscriptionInfo) {
        this.flags.add(WidgetFlag.HAS_SUBSCRIPTION_INFO)
      } else {
        this.flags.remove(WidgetFlag.HAS_SUBSCRIPTION_INFO)
      }

      if (this.customerInfo) {
        this.flags.add(WidgetFlag.HAS_CUSTOMER_INFO)
      } else {
        this.flags.remove(WidgetFlag.HAS_CUSTOMER_INFO)
      }

      if (this.epmsPaymentInfo) {
        this.flags.add(WidgetFlag.HAS_EPMS_PAYMENT_INFO)
      } else {
        this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_INFO)
      }

      if (this.epmsSubscriptionInfo) {
        this.flags.add(WidgetFlag.HAS_EPMS_SUBSCRIPTION_INFO)
      } else {
        this.flags.remove(WidgetFlag.HAS_EPMS_SUBSCRIPTION_INFO)
      }

      if (this.epmsPaymentSourceInfo) {
        this.flags.add(WidgetFlag.HAS_EPMS_PAYMENT_SOURCE_INFO)
      } else {
        this.flags.remove(WidgetFlag.HAS_EPMS_PAYMENT_SOURCE_INFO)
      }

      if (this.isSmsPollOverlayShown()) {
        this.flags.add(WidgetFlag.SHOW_OVERLAY_SMS_POLL)
      } else {
        this.flags.remove(WidgetFlag.SHOW_OVERLAY_SMS_POLL)
      }
    })
  }

  private isSmsPollOverlayShown(): boolean {
    let isSmsPaymentMethod =
      this.transactionInfo &&
      this.transactionInfo.payment_method === PaymentMethod.SMS
    let isPending =
      this.getTransactionFinalStatus() === TransactionStatus.PENDING

    return isSmsPaymentMethod && isPending
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // RECAPTCHA
  //
  /////////////////////////////////////////////////////////////////////////////

  initRecaptcha() {
    this.recaptcha = {
      ref: createRef(),
      callback: (token) => this.recaptcha.resolve(token),
      promise: null,
      resolve: null,
    }
    this.recaptcha.promise = new Promise<string>((resolve) => {
      this.recaptcha.resolve = resolve
    })
  }

  /////////////////////////////////////////////////////////////////////////////

  private registerCompilerHelpers() {
    const form = this.paymentForm

    const purpose = () => form.getPurpose()
    const paymentType = () => form.getPaymentType()
    const recurringInterval = () => form.getRecurringInterval()
    const currency = () => form.getCurrency()

    const amount = () => form.getAmount()
    const feeAmount = () => form.getFeeAmount()
    const totalAmount = () => form.getTotalAmount()

    const formattedAmount = () => {
      return formatAmount(
        form.getAmount(),
        form.getCurrency(),
        this.computedConfig,
      )
    }

    const formattedTotalAmount = () => {
      return formatAmount(
        form.getTotalAmount(),
        form.getCurrency(),
        this.computedConfig,
      )
    }

    const formattedFeeAmount = () => {
      return formatAmount(
        form.getFeeAmount(),
        form.getCurrency(),
        this.computedConfig,
      )
    }

    const paymentMethod = () => form.getPaymentMethod()

    const isCreditCard = (method: PaymentMethod) =>
      isCreditCardPaymentMethod(method)

    const paymentForm = (key) => {
      let value = get(form.data, key, null)

      return value !== null ? value : '{' + key + '}'
    }

    const showPaymentAddress = (): boolean => {
      let paymentMethod = form.getPaymentMethod()
      let paymentMethods = [
        PaymentMethod.DIRECT_DEBIT,
        PaymentMethod.PAYMENT_SLIP,
        PaymentMethod.PAYMENT_SLIP_WITH_REFERENCE_NUMBER,
      ]

      return (
        form.data.stored_customer_donation_receipt === true ||
        paymentMethods.indexOf(paymentMethod) !== -1 ||
        isCreditCardPaymentMethod(paymentMethod)
      )
    }

    const isPostFinanceIban = (): boolean => {
      const iban = form.data.iban

      return isString(iban) && iban.replace(/\s/g, '').slice(4, 9) === '09000'
    }

    const trans = (key: string | string[], options?: TOptions) => {
      return this.trans(key, options)
    }

    const config = (key) => {
      let value = get(this.computedConfig, key, null)

      return value !== null ? value : '{' + key + '}'
    }

    const resolveConfig = (key) => this.resolveConfig(key)
    const resolveConfigWithSingleResult = (key) =>
      this.resolveConfigWithSingleResult(key)

    const transactionInfo = (key) => {
      let value = get(this.transactionInfo, key, null)

      return value !== null ? value : '{' + key + '}'
    }

    const subscriptionInfo = (key) => {
      let value = get(this.subscriptionInfo, key, null)

      return value !== null ? value : '{' + key + '}'
    }

    const transactionFinalStatus = () => this.getTransactionFinalStatus()

    const isPayu = () => {
      return (
        isCreditCardPaymentMethod(this.paymentForm.getPaymentMethod()) &&
        this.computedConfig.paymentProvider === PaymentProvider.PAYU
      )
    }

    // register lookups that use api internally
    this.compiler.addMethods({
      // form helpers
      purpose,
      paymentType,
      recurringInterval,
      currency,
      amount,
      feeAmount,
      totalAmount,
      formattedAmount,
      formattedTotalAmount,
      formattedFeeAmount,
      paymentMethod,
      isCreditCard,
      paymentForm,
      showPaymentAddress,
      isPostFinanceIban,
      isPayu,

      // other helpers
      trans,
      config,
      resolveConfig,
      resolveConfigWithSingleResult,
      transactionInfo,
      subscriptionInfo,
      transactionFinalStatus,

      // deprecated helpers (to keep backwards compatibility)
      fee: formattedFeeAmount,
    })
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // OTHER
  //
  /////////////////////////////////////////////////////////////////////////////

  resolveConfig<T = string>(
    key: keyof WidgetConfig,
    mocks: FunctionMocks = null,
  ): T[] {
    return uniq(this.compiler.resolveCondition(this.computedConfig[key], mocks))
  }

  resolveConfigWithSingleResult<T = string>(
    key: keyof WidgetConfig,
    mocks: FunctionMocks = null,
  ): T {
    return this.compiler.resolveConditionWithSingleResult(
      this.computedConfig[key],
      mocks,
    )
  }

  /////////////////////////////////////////////////////////////////////////////

  trans(
    key: string | string[],
    options: TOptions = null,
    mocks: FunctionMocks = null,
  ): any {
    return this.translator.getTranslation(
      this.computedConfig.language,
      key,
      options,
      mocks,
    )
  }

  getTransactionFinalStatus(): TransactionStatus {
    if (!this.transactionInfo) {
      return null
    }

    return this.transactionInfo.epayment_status
  }

  /////////////////////////////////////////////////////////////////////////////

  async forceCancelTransaction() {
    this.epik.transactionStatusPoll.stop()

    if (this.transactionInfo) {
      this.transactionInfo.epayment_status = TransactionStatus.CANCEL
    }

    await this.tryEmitPaymentCompleteEvent()
  }

  async tryEmitPaymentCompleteEvent() {
    if (this.transactionInfo) {
      let finalStatus = this.getTransactionFinalStatus()

      if (finalStatus && finalStatus !== TransactionStatus.PENDING) {
        await this.events[WidgetEventName.PAYMENT_COMPLETE].publish({api: this})

        if (this.transactionInfo.old_subscription_token) {
          await this.events[
            WidgetEventName.SUBSCRIPTION_UPDATE_COMPLETE
          ].publish({api: this})
        }
      }
    }

    if (this.epmsPaymentInfo) {
      if (this.epmsPaymentInfo.last_status === EpmsPaymentStatus.SUCCEEDED) {
        await this.events[WidgetEventName.PAYMENT_COMPLETE].publish({api: this})
      }
    }
  }

  /////////////////////////////////////////////////////////////////////////////

  async cancelSubscription() {
    if (this.subscriptionInfo) {
      let subscriptionToken = this.subscriptionInfo.subscription_token

      try {
        this.flags.add(WidgetFlag.SHOW_SPINNER_OUTER)
        const subscriptionInfo = await this.epik.cancelSubscription(
          subscriptionToken,
        )
        this.setSubscriptionInfo(subscriptionInfo)
        await this.events[WidgetEventName.SUBSCRIPTION_CANCELLED].publish({
          api: this,
        })
      } finally {
        this.flags.remove(WidgetFlag.SHOW_SPINNER_OUTER)
      }
    }

    if (this.epmsSubscriptionInfo) {
      let epmsSubscriptionUuid = this.epmsSubscriptionInfo.uuid

      try {
        this.flags.add(WidgetFlag.SHOW_SPINNER_OUTER)
        await this.epik.cancelEpmsSubscription(epmsSubscriptionUuid)
        const epmsSubscriptionInfo = await this.epik.fetchEpmsSubscription(
          epmsSubscriptionUuid,
        )
        this.setEpmsSubscriptionInfo(epmsSubscriptionInfo)
        await this.events[WidgetEventName.SUBSCRIPTION_CANCELLED].publish({
          api: this,
        })
      } finally {
        this.flags.remove(WidgetFlag.SHOW_SPINNER_OUTER)
      }
    }
  }
}

///////////////////////////////////////////////////////////////////////////////

export function createWidgetApi(args: WidgetApiArgs): WidgetApi {
  return new WidgetApi(args)
}
